import selectors from '../rootSelectors';
import { formatBookTitle } from './troUtils';
import { FORMATTED_GRADES } from '../constants/book';
import { XAPI_VERBS, XAPI_OBJECTS } from '../constants/events';
import { TRO } from '../constants/measure';
import { TRO_COMPREHENSION_SECTION, TRO_READING, TRO_RECORDING, TRO_PAGE_RECORDING, TRO_PRE_ASSESSMENT_STEPS } from '../common/timeTracking/timeTrackingKeys';
import LetterGenerator from '../common/utils/letterGeneratorUtil';

const SECONDS_TO_MS_FACTOR = 1000;

export const types = {
    TRACK_TRO_EVENT: 'TRACK_TRO_EVENT',
};

// XAPI EVENTS

export const trackTROEvent = ({ verbId, objectId, additionalContext = {}, resultConfig, error = null}) =>
    async (dispatch, getStore) => {
        const state = getStore();
        const period = selectors.getToy(state);
        const schoolYear = selectors.getSchoolYear(state);
        const grade = selectors.getGrade(state);
        const troResult = selectors.findResultFor(state, { measure: TRO.MEASURE });
        const resultId = troResult ? troResult.id : null;
        const isExternalURLSourcePresent = selectors.getExternalURLSourceStatus(state);
        const userId = isExternalURLSourcePresent ? selectors.getAquaId(state) : selectors.getUserId(state);
        const isBenchmarkMode = selectors.getisBenchmarkModeEnabled(state);
        const arTROContext = {};

        if (isExternalURLSourcePresent) {
            const benchmarkId = selectors.getBenchmarkId(state);
            arTROContext['benchmark-assessment-guid'] = benchmarkId;
            arTROContext.benchmarkMode = isBenchmarkMode;
        }

        const contextConfig = {
            'user-enrollment-source': isExternalURLSourcePresent ? 'onboarding' : 'mclass',
            'user-enrollment-grade': FORMATTED_GRADES[grade] || grade,
            'time-of-year': period,
            'school-year': schoolYear,
            'result-id': resultId,
            ...arTROContext,
            ...additionalContext,
        };

        const troAction = {
            type: types.TRACK_TRO_EVENT,
            userId,
            verbId,
            objectId,
            contextConfig,
            resultConfig,
        };

        if (error) {
            troAction.error = error;
        }

        await dispatch(troAction);
    };

export const trackAnsweredTroQuestion = questionIndex => (dispatch, getStore) => {
    const store = getStore();
    const isExternalURLSourcePresent = selectors.getExternalURLSourceStatus(store);
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const comprehensionAttempts = selectors.getTRCComprehensionAttempts(store);
    const questionDuration = selectors.getQuestionTimeTrackingDuration(store) * SECONDS_TO_MS_FACTOR;

    const { possibleResponses } = selectors.getTRCComprehensionItem(store, questionIndex);
    const lettersArray = LetterGenerator(possibleResponses.length);
    const correctResponses = possibleResponses.filter(response => response.isCorrect);
    const correctResponseLetters = correctResponses.map(response => lettersArray[response.id]);
    const correctResponseText = correctResponses.map(response => response.text);
    const studentResponse = selectors.getTRCSelectedResponses(store)[questionIndex];
    const studentResponseLetters = Array.isArray(studentResponse)
        ? studentResponse.map(response => lettersArray[response])
        : [ lettersArray[studentResponse] ];
    const studentResponseText = Array.isArray(studentResponse)
        ? studentResponse.map(response => possibleResponses[response].text)
        : [ possibleResponses[studentResponse].text ];

    const isCorrect = correctResponseLetters.every((letter, idx) => letter === studentResponseLetters[idx]);
    const pagesViewed = selectors.getTRCPagesViewed(store);
    const didStudentViewText = pagesViewed.length;

    const correctResponseObj = {
        'response-position': correctResponseLetters,
        'response-text': correctResponseText
    };
    const studentResponseObj = {
        'response-position': studentResponseLetters,
        'response-text': studentResponseText
    };

    const additionalContext = {
        'book-id': bookId,
        'book-title': bookTitle,
        'question-id': questionIndex,
        'question-number': questionIndex + 1,
        'student-response-number': comprehensionAttempts[questionIndex] || 1,
        'correct-response': JSON.stringify(correctResponseObj),
        'time-to-response': questionDuration,
    };

    const resultConfig = {
        'student-response': JSON.stringify(studentResponseObj),
        'student-response-is-correct': isCorrect,
        'student-viewed-pages': !!didStudentViewText,
        'pages-viewed': didStudentViewText ? pagesViewed : '',
        ...(!isExternalURLSourcePresent && {'button-description': 'next'})
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.ANSWERED,
        objectId: `${XAPI_OBJECTS.TRO}-${formattedBookTitle}-question-${questionIndex}`,
        additionalContext,
        resultConfig,
    }));
};

export const trackTroBookIsCompleted  = () => (dispatch, getStore) => {
    const state = getStore();
    const timeToCompletion = selectors.getTimeTrackingDuration(state, TRO_COMPREHENSION_SECTION) * SECONDS_TO_MS_FACTOR;
    const bookId = selectors.getCurrentBookId(state);
    const selectedResponses = selectors.getTRCBookResults(state);
    const getLastBook = selectedResponses.length-1;
    const answeredQuestion = selectedResponses[getLastBook].responsesQuestions.length;
    const {bookTitle, comprehensionScore, vrScore, isBookPassed} = selectedResponses[getLastBook];
    const formattedBookTitle = bookTitle.toLowerCase().split(' ').join('-');
    const userScoreArray = selectedResponses[getLastBook].responsesQuestions.reduce((answer, item) => {
        // eslint-disable-next-line no-param-reassign
        answer[item.item_idx] = {
            'student-response': item.response,
            'student-response-is-correct': Boolean(item.score)
        };
        return answer;
    }, {});

    const additionalContext = {
        'book-id': bookId,
        'book-title': bookTitle,
        'time-to-completion': timeToCompletion,
    };
    const resultConfig = {
        'number-of-items-answered': answeredQuestion,
        'student-question-responses': userScoreArray,
        'fluency-score': vrScore,
        'comprehension-score': comprehensionScore,
        'is-book-passed': isBookPassed
    };
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.COMPLETED,
        objectId: `${XAPI_OBJECTS.TRO}-${formattedBookTitle}`,
        additionalContext,
        resultConfig
    }));
};

export const trackStartTroQuestions = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const isExternalURLSourcePresent = selectors.getExternalURLSourceStatus(store);
    const objectId = isExternalURLSourcePresent
        ? `${XAPI_OBJECTS.TRO}-${formattedBookTitle}-questions` : 'TROComprehension';
    const additionalContext = {
        'book-id': bookId,
        'book-title': bookTitle,
        ...(isExternalURLSourcePresent && {stage: 'reading'}),
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.STARTED,
        objectId,
        additionalContext,
    }));
};


// mCLASS TRO xAPI Events

export const trackStartTroReading = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);

    const additionalContext = {
        'book-id': bookId,
        'book-title': formattedBookTitle,
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.STARTED,
        objectId: `${XAPI_OBJECTS.TRO}-${formattedBookTitle}`,
        additionalContext,
    }));
};

export const trackCompleteTroReading = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);

    const additionalContext = {
        'book-id': bookId,
        'book-title': formattedBookTitle,
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.READ,
        objectId: `${XAPI_OBJECTS.TRO}-${formattedBookTitle}`,
        additionalContext,
    }));
};

export const trackTroReadingNoButton = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);

    const additionalContext = {
        'book-id': bookId,
        'book-title': formattedBookTitle,
    };

    const resultConfig = {
        'button-description': 'no'
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.PRESSED,
        objectId: `${XAPI_OBJECTS.TRO}-${formattedBookTitle}`,
        additionalContext,
        resultConfig
    }));
};

export const trackTroReadingNavigationButton = (page, buttonType) => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const formattedPage = page + 1;

    const additionalContext = {
        'book-id': bookId,
        'book-title': formattedBookTitle,
        'page': formattedPage,
    };

    const resultConfig = {
        'button-description': buttonType
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.PRESSED,
        objectId: `${XAPI_OBJECTS.TRO}-book-${formattedBookTitle}-page-${formattedPage}`,
        additionalContext,
        resultConfig
    }));
};

export const trackStartTroPageRecording = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const formattedPage = selectors.getActiveTRCPage(store) + 1;

    const additionalContext = {
        'book-id': bookId,
        'book-title': formattedBookTitle,
        'page': formattedPage,
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.VIEWED,
        objectId: `${XAPI_OBJECTS.TRO}-book-${formattedBookTitle}-page-${formattedPage}`,
        additionalContext,
    }));
};

export const trackStopTroPageRecording = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const formattedPage = selectors.getActiveTRCPage(store) + 1;

    const additionalContext = {
        'book-id': bookId,
        'book-title': formattedBookTitle,
        'page': formattedPage,
    };

    const resultConfig = {
        'button-description': 'stop-recording'
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.RECORDED,
        objectId: `${XAPI_OBJECTS.TRO}-book-${formattedBookTitle}-page-${formattedPage}`,
        additionalContext,
        resultConfig
    }));
};

export const trackCompleteTroPageRecording = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const formattedPage = selectors.getActiveTRCPage(store) + 1;
    const shouldUsePreciseTime = true;
    const recordingDuration = selectors.getTimeTrackingDuration(store, TRO_PAGE_RECORDING, shouldUsePreciseTime) * SECONDS_TO_MS_FACTOR;

    const additionalContext = {
        'book-id': bookId,
        'book-title': formattedBookTitle,
        'page': formattedPage,
        'recording-duration-ms': recordingDuration
    };

    const resultConfig = {
        'recording-status': 'in-progress'
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.RECORDED,
        objectId: `${XAPI_OBJECTS.TRO}-book-${formattedBookTitle}-page-${formattedPage}`,
        additionalContext,
        resultConfig
    }));
};

export const trackCompleteTroBookRecording = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const formattedPage = selectors.getActiveTRCPage(store) + 1;
    const recordingDuration = selectors.getBookRecordingDurationMs(store);

    const additionalContext = {
        'book-id': bookId,
        'book-title': formattedBookTitle,
        'page': formattedPage,
        'recording-duration-ms': recordingDuration
    };

    const resultConfig = {
        'recording-status': 'complete'
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.RECORDED,
        objectId: `${XAPI_OBJECTS.TRO}-book-${formattedBookTitle}-page-${formattedPage}`,
        additionalContext,
        resultConfig
    }));
};

export const trackTroComprehensionBackButton = questionIndex => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);

    const additionalContext = {
        'book-id': bookId,
        'book-title': formattedBookTitle,
        'question-id': questionIndex,
        'question-number': questionIndex + 1,
    };

    const resultConfig = {'button-description': 'next'};

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.PRESSED,
        objectId: `${XAPI_OBJECTS.TRO}-${formattedBookTitle}-question-${questionIndex}`,
        additionalContext,
        resultConfig
    }));
};

export const trackTroVrPopup = (problemText, bookId = null, bookTitle = null) => dispatch => {
    const formattedBookTitle = bookTitle ? formatBookTitle(bookTitle) : '';

    const additionalContext = {
        'page-url': window.location.href,
        'book-id': bookId,
        'book-title': formattedBookTitle,
        'popup-problem-text': problemText,
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.VIEWED,
        objectId: `${XAPI_OBJECTS.TRO}-voice-recording`,
        additionalContext,
    }));
};

// AR TRO xAPI Events

export const trackTroError = errorMessage => async dispatch => {
    const additionalContext = {
        assessment: 'AR-TRO',
    };
    const resultConfig = {
        'error-message': errorMessage,
    };
    await dispatch(trackTROEvent({
        verbId: XAPI_VERBS.ENCOUNTERED,
        objectId: `${XAPI_OBJECTS.TRO}-error`,
        additionalContext,
        resultConfig,
        error: errorMessage,
    }));
};

export const trackStartAssessment = () => (dispatch) => {
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.STARTED,
        objectId: XAPI_OBJECTS.TRO,
    }));
};

export const trackTroMicCheck = () => (dispatch, getStore) => {
    const state = getStore();
    const isMicCheckPassed = selectors.isMicCheckPassed(state);
    const resultConfig = {
        'is-microphone-check-passed': isMicCheckPassed,
    };
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.COMPLETED,
        objectId: `${XAPI_OBJECTS.TRO}-microphone-check`,
        resultConfig,
    }));
};

export const trackViewedTroBook = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const formattedPage = selectors.getActiveTRCPage(store) + 1;
    const additionalContext = {
        'book-id': bookId,
        'book-title': bookTitle,
        stage: 'reading',
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.VIEWED,
        objectId: `${XAPI_OBJECTS.TRO}-book-${formattedBookTitle}-page-${formattedPage}`,
        additionalContext,
    }));
};

export const trackRecordedTroBook = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const formattedPage = selectors.getActiveTRCPage(store) + 1;
    const recordingDuration = selectors.getTimeTrackingDuration(store, TRO_RECORDING) * SECONDS_TO_MS_FACTOR;
    const additionalContext = {
        'book-id': bookId,
        'book-title': bookTitle,
        stage: 'reading',
        'recording-time-elapsed': `${recordingDuration}-ms`,
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.RECORDED,
        objectId: `${XAPI_OBJECTS.TRO}-book-${formattedBookTitle}-page-${formattedPage}`,
        additionalContext,
    }));
};

export const trackReadTroBook = () => (dispatch, getStore) => {
    const store = getStore();
    const bookId = selectors.getTRCBookId(store);
    const bookTitle = selectors.getTRCBookTitle(store);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const readingDuration = selectors.getTimeTrackingDuration(store, TRO_READING) * SECONDS_TO_MS_FACTOR;
    const additionalContext = {
        'book-id': bookId,
        'book-title': bookTitle,
        stage: 'reading',
        'reading-time-elapsed': `${readingDuration}-ms`,
    };

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.READ,
        objectId: `${XAPI_OBJECTS.TRO}-${formattedBookTitle}`,
        additionalContext,
    }));
};

export const trackTROBookStart = () => (dispatch, getStore) => {
    const state = getStore();
    const bookTitle = selectors.getTRCBookTitle(state);
    const formattedBookTitle = formatBookTitle(bookTitle);
    const bookId = selectors.getCurrentBookId(state);
    const relativeLevel = selectors.getTRCBookRelativeLevel(state);
    const readingLevel = selectors.getTRCReadingLevel(state);
    const genre = selectors.getTRCBookGenre(state);
    const additionalContext = {
        'book-id': bookId,
        'book-title': bookTitle,
        'relative-level': relativeLevel,
        'reading-level': readingLevel,
        'genre': genre,
    };
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.STARTED,
        objectId: `${XAPI_OBJECTS.TRO}-${formattedBookTitle}`,
        additionalContext
    }));
};

export const trackTROBooksComplete = () => (dispatch, getStore) => {
    const state = getStore();
    const bookResults = selectors.getTRCBookResults(state);
    const bookDuration = selectors.getReadingDuration(state);
    const timeToCompletition = bookDuration.reduce((prev, book) => {
        return prev.bookDuration + book.bookDuration;
    });
    const additionalContext = {
        'time-to-completion': `${timeToCompletition}-ms`,
    };

    const resultConfig = bookResults.reduce((acc, book, index) => {
        acc[`book${index + 1}-score`] = {
            'book-title': book.bookTitle,
            'book-id': book.bookId,
            'relative-level': book.relativeLevel,
            'reading-level': book.readingLevel,
            'genre': book.genre,
            'comprehension-score': book.comprehensionScore,
            'fluency-score': book.vrScore,
            'is-book-passed': book.isBookPassed,
        };
        return acc;
    }, {});

    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.COMPLETED,
        objectId: XAPI_OBJECTS.TRO,
        additionalContext,
        resultConfig,
    }));
};

export const trackFailedMicReadingCheck = () => (dispatch) => {
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.FAILED,
        objectId: "text-reading-online-check-microphone",
    }));
};

export const trackFailedEnableMic = () => dispatch => {
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.VIEWED,
        objectId: "text-reading-online-error-microphone",
    }));
};

export const trackFailedMicCheck = (attempt) => dispatch => {
    const additionalContext = { attempt };
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.FAILED,
        objectId: "text-reading-online-error-microphone",
        additionalContext,
    }));
};

export const trackPressedRestartButton = () => dispatch => {
    const additionalContext = { href: window.location.href };
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.PRESSED,
        objectId: "text-reading-online-error-microphone",
        additionalContext
    }));
};

export const trackPassedReadingCheck = () => (dispatch) => {
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.PASSED,
        objectId: "text-reading-online-check-reading",
    }));
};

export const trackTROFailedMultiChoiceReadingCheck = () => (dispatch) => {
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.FAILED,
        objectId: "text-reading-online-check-multiple-choice",
    }));
};

export const trackStartPreAssessmentStep = (section) => dispatch => {
    const additionalContext = { section };
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.STARTED,
        objectId: "text-reading-online-pre-assessment",
        additionalContext,
    }));
};

export const trackNextPreAssesmentStep = (section) => (dispatch) => {
    const additionalContext = { section };
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.COMPLETED,
        objectId: "text-reading-online-pre-assessment",
        additionalContext,
    }));
};

export const trackPauseDemoVideo = () => (dispatch) => {
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.PRESSED,
        objectId: "text-reading-online-demo-pause",
    }));
};

export const trackRestartDemoVideo = () => (dispatch) => {
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.PRESSED,
        objectId: "text-reading-online-demo-replay",
    }));
};

export const trackCompletedPreAssessment = () => (dispatch, getStore) => {
    const store = getStore();
    const additionalContext = {
        didPreviouslyEnableMic: selectors.getDidPreviouslyEnableMic(store),
        duration: selectors.getTimeTrackingDuration(store, TRO_PRE_ASSESSMENT_STEPS),
    };
    dispatch(trackTROEvent({
        verbId: XAPI_VERBS.COMPLETED,
        objectId: `text-reading-online-pre-assessment`,
        additionalContext,
    }));
};
